/**
 * 要注意フィルタ（#3262）。
 *
 * Firestore の複合インデックスを増やさないため、一覧の「要注意のみ」絞り込みは
 * クライアントサイドで行う（設計書 §6.4）。
 * ファイル名検索・フリーワード検索と同じ「全件取得 → JSでフィルタ」の経路に乗せている。
 */

import type { DocumentData } from 'firebase/firestore';
import { needsReviewFilterType, ocrStatus, processingStatus } from '../../constants/enum';

/**
 * 結果が要注意として扱われるか（#3262）。
 *
 * 一覧のバッジ表示・絞り込みの両方で同じ判定を使う。
 *
 * needs_review が undefined のもの（本機能リリース前のデータ）は
 * 「判定していない」として要注意にしない。
 *
 * 確認済・出力済のものはバッジを出さない。要注意の情報は Firestore に
 * 保持したまま、人が確認を終えたものは UI で出し分ける（設計書 §4.7）。
 */
export const isNeedsReviewResult = (result: DocumentData | undefined | null): boolean => {
  if (!result) return false;
  if (result.needs_review !== true) return false;

  // 読み取りが完了していないものは判定が確定していないため出さない
  if (result.ocr_status !== ocrStatus.completed) return false;

  // 人が確認を終えたものは出さない
  return (
    result.processing_status === processingStatus.draft ||
    result.processing_status === processingStatus.processing
  );
};

/**
 * 要注意で絞り込む（#3262）。
 *
 * 「要注意以外」は、一括で確認済にする運用のために用意している
 * （絞り込んで全選択し、一括操作の「確認済にする」を使う）。
 *
 * @param results 絞り込み対象
 * @param filterType needsReviewFilterType の値。未指定・all なら素通し
 */
export const filterByNeedsReview = <T extends DocumentData>(
  results: T[],
  filterType?: number,
): T[] => {
  if (filterType === needsReviewFilterType.only) {
    return results.filter(isNeedsReviewResult);
  }
  if (filterType === needsReviewFilterType.exclude) {
    // 「要注意以外」は読取完了かつ未処理のもののみを対象にする。
    // 一括で確認済にする前提のため、処理対象になりえないものは含めない
    return results.filter(
      result =>
        !isNeedsReviewResult(result) &&
        result.ocr_status === ocrStatus.completed &&
        (result.processing_status === processingStatus.draft ||
          result.processing_status === processingStatus.processing),
    );
  }
  return results;
};
